"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useNotificationPreferences = void 0;
const react_1 = require("react");
const mutations_1 = require("../graphql/mutations");
const queries_1 = require("../graphql/queries");
const types_1 = require("../types");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
const throwIfGraphqlErrors = (response) => {
    if (response?.errors?.length) {
        throw new Error(response.errors.map((e) => e.message).join('; '));
    }
};
const toChannelInput = (channel) => ({
    enabled: channel.enabled,
    notifyOnResolve: channel.notifyOnResolve,
    water: channel.water,
    power: channel.power,
    hvac: channel.hvac,
    airQuality: channel.airQuality,
    connectivity: channel.connectivity,
});
/**
 * Read/write the caller's own notification preferences. The backend derives
 * the user from the Cognito identity, so there is no userId argument. A user
 * who has never saved gets the all-on defaults.
 */
const useNotificationPreferences = () => {
    const [data, setData] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [saving, setSaving] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const refetch = (0, react_1.useCallback)(async () => {
        setLoading(true);
        try {
            const response = await amplifyBootstrap_1.API.graphql({ query: queries_1.getMyNotificationPreferences });
            throwIfGraphqlErrors(response);
            const prefs = response.data?.getMyNotificationPreferences;
            // Defensive merge: an older backend (or a partial row) still yields a
            // fully-populated object so the settings screen never renders holes.
            setData((0, types_1.withNotificationPreferenceDefaults)(prefs?.userId ?? '', prefs));
            setError(null);
        }
        catch (err) {
            setError(err instanceof Error ? err : new Error(String(err)));
            // A backend that can't answer (older schema, network) must not strand
            // the screen on a spinner: fall back to the all-on defaults, which is
            // exactly what the dispatcher assumes for a user with no stored row.
            setData((current) => current ?? (0, types_1.withNotificationPreferenceDefaults)(''));
        }
        finally {
            setLoading(false);
        }
    }, []);
    const save = (0, react_1.useCallback)(async (prefs) => {
        setSaving(true);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateMyNotificationPreferences,
                variables: {
                    input: {
                        sms: toChannelInput(prefs.sms),
                        email: toChannelInput(prefs.email),
                        push: toChannelInput(prefs.push),
                    },
                },
            });
            throwIfGraphqlErrors(response);
            const saved = response.data?.updateMyNotificationPreferences;
            setData((0, types_1.withNotificationPreferenceDefaults)(saved?.userId ?? prefs.userId, saved ?? prefs));
            setError(null);
            return true;
        }
        catch (err) {
            setError(err instanceof Error ? err : new Error(String(err)));
            return false;
        }
        finally {
            setSaving(false);
        }
    }, []);
    (0, react_1.useEffect)(() => {
        refetch();
    }, [refetch]);
    return { data, loading, saving, error, refetch, save };
};
exports.useNotificationPreferences = useNotificationPreferences;
